import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import PasswordInput from '../ui/PasswordInput';
import { toast } from 'sonner';
import { DatabaseIcon, SearchIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, RefreshCwIcon, PlusIcon, UserPlusIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UltraCollections = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dataSearchQuery, setDataSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  
  // User creation modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    company_id: '',
    role_id: '',
    is_verified: true,
    is_active: true
  });
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchCollectionData(selectedCollection.name, page * limit);
    }
  }, [selectedCollection, page]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/collections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(response.data.collections);
    } catch (error) {
      toast.error('Collection\'lar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionData = async (collectionName, skip = 0) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/collections/${collectionName}?skip=${skip}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCollectionData(response.data);
    } catch (error) {
      toast.error('Collection verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Bu dökümanı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('ultra_admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/ultra-admin/collections/${selectedCollection.name}/document?document_id=${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Döküman silindi');
      fetchCollectionData(selectedCollection.name, page * limit);
      fetchCollections(); // Refresh counts
    } catch (error) {
      console.error('Document delete error:', error);
      const errorDetail = error.response?.data?.detail;
      
      // Handle object errors (convert to string)
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        toast.error(typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail));
      } else {
        toast.error(errorDetail || 'Döküman silinirken hata oluştu');
      }
    }
  };

  const fetchFormData = async () => {
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/collections/users/form-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Form data fetch error:', error);
      toast.error('Form verileri yüklenirken hata oluştu');
      setCompanies([]);
      setRoles([]);
    }
  };

  const handleOpenUserModal = () => {
    setShowUserModal(true);
    fetchFormData();
    setUserFormData({
      email: '',
      full_name: '',
      password: '',
      company_id: '',
      role_id: '',
      is_verified: true,
      is_active: true
    });
  };

  const handleCompanyChange = (companyId) => {
    setUserFormData({ ...userFormData, company_id: companyId, role_id: '' });
    const companyRoles = (roles || []).filter(r => r.company_id === companyId);
    setFilteredRoles(companyRoles);
  };

  const handleCreateUser = async () => {
    if (!userFormData.email || !userFormData.full_name || !userFormData.password || !userFormData.company_id || !userFormData.role_id) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('ultra_admin_token');
      await axios.post(
        `${BACKEND_URL}/api/ultra-admin/collections/users/create`,
        userFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kullanıcı başarıyla oluşturuldu');
      setShowUserModal(false);
      fetchCollectionData('users', page * limit);
      fetchCollections();
    } catch (error) {
      console.error('User creation error:', error);
      const errorDetail = error.response?.data?.detail;
      
      // Handle Pydantic validation errors
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (Array.isArray(errorDetail)) {
          // Array of validation errors
          const messages = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Validation error: ${messages}`);
        } else if (errorDetail.msg) {
          // Single validation error with msg field
          toast.error(errorDetail.msg);
        } else {
          // Other object error
          toast.error('Kullanıcı oluşturulurken hata oluştu: ' + JSON.stringify(errorDetail));
        }
      } else {
        // String error message
        toast.error(errorDetail || 'Kullanıcı oluşturulurken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredCollections = collections.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter documents based on search query
  const filteredDocuments = collectionData?.documents?.filter(doc => {
    if (!dataSearchQuery) return true;
    const jsonString = JSON.stringify(doc).toLowerCase();
    return jsonString.includes(dataSearchQuery.toLowerCase());
  }) || [];

  const totalPages = collectionData ? Math.ceil(collectionData.total / limit) : 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <DatabaseIcon className="w-8 h-8 mr-3 text-blue-600" />
          MongoDB Collections
        </h1>
        <p className="text-gray-600 mt-2">
          Veritabanındaki tüm collection'ları görüntüleyin ve yönetin
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Collections List */}
        <div className="col-span-12 lg:col-span-3">
          <Card className="p-4">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Collection ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Collections List */}
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
              {loading && !collectionData ? (
                <div className="text-center py-8">
                  <RefreshCwIcon className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : (
                filteredCollections.map((collection) => (
                  <button
                    key={collection.name}
                    onClick={() => {
                      setSelectedCollection(collection);
                      setPage(0);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedCollection?.name === collection.name
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm truncate">{collection.name}</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          collection.count === 0
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {collection.count}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Summary */}
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                <div className="flex justify-between mb-1">
                  <span>Toplam Collection:</span>
                  <span className="font-semibold">{collections.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Görüntülenen:</span>
                  <span className="font-semibold">{filteredCollections.length}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Panel - Collection Data */}
        <div className="col-span-12 lg:col-span-9">
          {!selectedCollection ? (
            <Card className="p-12 text-center">
              <DatabaseIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Collection Seçin
              </h3>
              <p className="text-gray-500">
                Sol taraftan bir collection seçerek içeriğini görüntüleyin
              </p>
            </Card>
          ) : (
            <Card className="p-6">
              {/* Collection Header */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                      {selectedCollection.name}
                      <span className="ml-3 text-sm font-normal px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                        {collectionData?.total || 0} döküman
                      </span>
                    </h2>
                  </div>
                  <div className="flex gap-2">
                    {selectedCollection.name === 'users' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleOpenUserModal}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <UserPlusIcon className="w-4 h-4 mr-2" />
                        Kullanıcı Ekle
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchCollectionData(selectedCollection.name, page * limit)}
                    >
                      <RefreshCwIcon className="w-4 h-4 mr-2" />
                      Yenile
                    </Button>
                  </div>
                </div>

                {/* Data Search */}
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Döküman içinde ara... (email, id, name, vb.)"
                    value={dataSearchQuery}
                    onChange={(e) => setDataSearchQuery(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {dataSearchQuery && (
                    <button
                      onClick={() => setDataSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {dataSearchQuery && (
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredDocuments.length} döküman bulundu
                  </p>
                )}
              </div>

              {/* Data Table */}
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCwIcon className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Yükleniyor...</p>
                </div>
              ) : collectionData?.documents?.length === 0 ? (
                <div className="text-center py-12">
                  <DatabaseIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Bu collection boş</p>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <SearchIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Arama sonucu bulunamadı</p>
                  <p className="text-sm text-gray-400 mt-2">"{dataSearchQuery}" için sonuç yok</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <div className="space-y-3">
                      {filteredDocuments.map((doc, index) => (
                        <div
                          key={doc._id || index}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <code className="text-xs text-gray-500 block">
                                ID: {doc.id || doc._id}
                              </code>
                              {/* Special info for users collection */}
                              {selectedCollection.name === 'users' && doc.email && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    📧 {doc.email}
                                  </span>
                                  {doc.full_name && (
                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                      👤 {doc.full_name}
                                    </span>
                                  )}
                                  {doc.is_verified !== undefined && (
                                    <span className={`text-xs px-2 py-1 rounded ${doc.is_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {doc.is_verified ? '✅ Doğrulanmış' : '❌ Doğrulanmamış'}
                                    </span>
                                  )}
                                  {doc.is_active !== undefined && (
                                    <span className={`text-xs px-2 py-1 rounded ${doc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                      {doc.is_active ? '🟢 Aktif' : '⚫ Pasif'}
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Special info for companies collection */}
                              {selectedCollection.name === 'companies' && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  {doc.company_name && (
                                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                      🏢 {doc.company_name}
                                    </span>
                                  )}
                                  {doc.subscription_plan && (
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                      📦 {doc.subscription_plan}
                                    </span>
                                  )}
                                  {doc.subscription_type && (
                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                      💳 {doc.subscription_type === 'monthly' ? 'Aylık' : 'Yıllık'}
                                    </span>
                                  )}
                                  {doc.payment_status && (
                                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                                      💰 {doc.payment_status}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id || doc._id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          </div>
                          <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                            {JSON.stringify(doc, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-6 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Sayfa {page + 1} / {totalPages} ({collectionData.total} toplam)
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(Math.max(0, page - 1))}
                          disabled={page === 0}
                        >
                          <ChevronLeftIcon className="w-4 h-4" />
                          Önceki
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                          disabled={page >= totalPages - 1}
                        >
                          Sonraki
                          <ChevronRightIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* User Creation Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <UserPlusIcon className="w-6 h-6 mr-2 text-green-600" />
                  Yeni Kullanıcı Oluştur
                </h2>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Email */}
                <div>
                  <Label htmlFor="email">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  />
                </div>

                {/* Full Name */}
                <div>
                  <Label htmlFor="full_name">Ad Soyad *</Label>
                  <Input
                    id="full_name"
                    placeholder="Kullanıcının tam adı"
                    value={userFormData.full_name}
                    onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                  />
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password">Şifre *</Label>
                  <PasswordInput
                    id="password"
                    placeholder="Güçlü bir şifre belirleyin"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">En az 6 karakter</p>
                </div>

                {/* Company */}
                <div>
                  <Label htmlFor="company">Şirket *</Label>
                  <Select value={userFormData.company_id} onValueChange={handleCompanyChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Şirket seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Role */}
                <div>
                  <Label htmlFor="role">Rol *</Label>
                  <Select
                    value={userFormData.role_id}
                    onValueChange={(value) => setUserFormData({ ...userFormData, role_id: value })}
                    disabled={!userFormData.company_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={userFormData.company_id ? "Rol seçin" : "Önce şirket seçin"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(filteredRoles || []).length > 0 ? (
                        filteredRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">
                          Bu şirket için rol tanımlı değil
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Checkboxes */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_verified"
                      checked={userFormData.is_verified}
                      onChange={(e) => setUserFormData({ ...userFormData, is_verified: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="is_verified" className="font-normal cursor-pointer">
                      Email doğrulanmış
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={userFormData.is_active}
                      onChange={(e) => setUserFormData({ ...userFormData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="is_active" className="font-normal cursor-pointer">
                      Aktif kullanıcı
                    </Label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowUserModal(false)}
                  disabled={loading}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <>
                      <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <UserPlusIcon className="w-4 h-4 mr-2" />
                      Kullanıcı Oluştur
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UltraCollections;
